import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react'
import type { DealFormData } from '../../../lib/types'

interface StepSecondaryMarketProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  headingId?: string
  onValidationError?: (errorMessage: string) => void
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  requestChanges?: Record<string, string>
}

export function StepSecondaryMarket({
  formData,
  onComplete,
  onUpdate,
  onBack,
  headingId,
  onValidationError,
  onValidationErrors,
  requestChanges,
}: StepSecondaryMarketProps) {
  const initialTransferability =
    (formData as any).nftTransferability || 'immediate'
  const initialRoyaltySplit = {
    project: ((formData as any).royaltySplit?.project as any) || '1',
    curator: ((formData as any).royaltySplit?.curator as any) || '1',
  }
  const [localData, setLocalData] = useState({
    nftTransferability: initialTransferability,
    customStartDateTime: (formData as any).customStartDateTime || '',
    royaltySplit:
      initialTransferability === 'never'
        ? { project: '0', curator: '0' }
        : initialRoyaltySplit,
    autoCalculateRoyalties: (formData as any).autoCalculateRoyalties || false,
  })

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const handleTransferabilityChange = (value: string) => {
    setLocalData((prev) => {
      const next = { ...prev, nftTransferability: value }
      if (value === 'never') {
        next.royaltySplit = { project: '0', curator: '0' }
      }
      onUpdate(next)
      return next
    })
    setFieldErrors((prev) => {
      const next = { ...prev }
      if (value !== 'custom') next.customStartDateTime = ''
      if (value === 'never') {
        next.project = ''
        next.curator = ''
      }
      return next
    })
  }

  const handleDateTimeChange = (value: string) => {
    if (!value) {
      setFieldErrors((prev) => ({
        ...prev,
        customStartDateTime: 'Start date & time is required',
      }))
      setLocalData((prev) => ({ ...prev, customStartDateTime: value }))
      onUpdate({ customStartDateTime: value } as any)
      return
    }
    const selectedDate = new Date(value + 'Z')
    const tgeValue = (formData as any).tgeTargetDate
    const tgeDate = tgeValue ? new Date((tgeValue as string) + 'Z') : null
    const now = new Date()
    if (selectedDate < now)
      setFieldErrors((prev) => ({
        ...prev,
        customStartDateTime: 'Date cannot be in the past',
      }))
    else if (tgeDate && selectedDate <= tgeDate)
      setFieldErrors((prev) => ({
        ...prev,
        customStartDateTime: 'Must be after TGE date & time',
      }))
    else setFieldErrors((prev) => ({ ...prev, customStartDateTime: '' }))
    setLocalData((prev) => ({ ...prev, customStartDateTime: value }))
    onUpdate({ customStartDateTime: value } as any)
  }

  const validateField = (value: string): string => {
    const numValue = Number.parseFloat(value)
    if (isNaN(numValue) || numValue < 0 || numValue > 100)
      return 'Enter a percentage between 0 and 100.'
    return ''
  }

  const formatValue = (value: string): string => {
    const numValue = Number.parseFloat(value)
    if (isNaN(numValue)) return ''
    return Math.round(numValue * 100) / 100 + ''
  }

  const handleInputChange = (field: string, value: string) => {
    const cleanValue = value.replace(/%/g, '')
    if (!/^\d*\.?\d*$/.test(cleanValue)) return
    updateRoyaltyField(field, cleanValue)
    if (fieldErrors[field]) setFieldErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const handleBlur = (field: string, value: string) => {
    const error = validateField(value)
    setFieldErrors((prev) => ({ ...prev, [field]: error }))
    if (!error) updateRoyaltyField(field, formatValue(value))
  }

  const isSoulBound = localData.nftTransferability === 'never'
  const isCustom = localData.nftTransferability === 'custom'
  const missingCustom = isCustom && !localData.customStartDateTime

  const updateRoyaltyField = (field: string, value: string) => {
    const numValue = Number.parseFloat(value) || 0
    if (localData.autoCalculateRoyalties && numValue > 0) {
      const otherFields = Object.keys(localData.royaltySplit).filter(
        (key) => key !== field,
      )
      const remainingPercentage = Math.max(0, 100 - numValue)
      const perFieldPercentage = remainingPercentage / otherFields.length
      const updatedRoyaltySplit: any = {
        ...localData.royaltySplit,
        [field]: value,
      }
      otherFields.forEach((otherField) => {
        updatedRoyaltySplit[otherField as any] = perFieldPercentage.toFixed(1)
      })
      setLocalData((prev) => ({ ...prev, royaltySplit: updatedRoyaltySplit }))
      onUpdate({ royaltySplit: updatedRoyaltySplit } as any)
    } else {
      setLocalData((prev) => {
        const next = {
          ...prev,
          royaltySplit: { ...prev.royaltySplit, [field]: value },
        }
        onUpdate({ royaltySplit: next.royaltySplit } as any)
        return next
      })
    }
  }

  const notifyError = () =>
    onValidationError?.(
      'Please complete all required fields before continuing.',
    )

  const handleSubmit = () => {
    if (missingCustom) {
      setFieldErrors((prev) => ({
        ...prev,
        customStartDateTime: 'Start date & time is required',
      }))
    }
    if (
      localData.nftTransferability === 'custom' &&
      localData.customStartDateTime
    ) {
      const tgeValue = (formData as any).tgeTargetDate
      const tgeDate = tgeValue ? new Date((tgeValue as string) + 'Z') : null
      const customDate = new Date(localData.customStartDateTime + 'Z')
      if (tgeDate && customDate <= tgeDate) {
        setFieldErrors((prev) => ({
          ...prev,
          customStartDateTime: 'Must be after TGE date & time',
        }))
      }
    }

    const currentErrors = { ...fieldErrors }
    if (missingCustom)
      currentErrors.customStartDateTime = 'Start date & time is required'

    const errorIds = Object.keys(currentErrors).filter(
      (key) => !!currentErrors[key],
    )

    if (errorIds.length === 0) {
      onValidationErrors?.([])
      onValidationError?.('')
      onComplete(localData as any)
    } else {
      const fieldLabels: Record<string, string> = {
        customStartDateTime: 'Custom Start Date',
        royaltySplit: 'Royalty Split',
        project: 'Project Royalty Split',
        curator: 'Curator Royalty Split',
      }

      const mappedErrors = errorIds.map((id) => ({
        id,
        label: fieldLabels[id] || id,
      }))

      if (onValidationErrors) {
        onValidationErrors(mappedErrors)
      } else {
        notifyError()
      }
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">NFT Transferability</CardTitle>
          <div className="text-sm text-neutral-600">
            <ul className="list-disc list-outside ml-4 space-y-1">
              <li>
                NFTs represent the users’ on-chain claims to tokens or yield
                distributions.
              </li>
              <li>
                Subject to transferability rules, NFTs can be wrapped and traded
                on secondary marketplaces. NFT wrapping freezes the metadata of
                the deals and disrupts token claiming/distribution.
              </li>
              <li>
                Users must always complete KYC or accreditation checks to unwrap
                and redeem their rights.
              </li>
              <li>
                Only unwrapped NFTs carry any actual token/yield claiming
                rights.
              </li>
            </ul>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="immediate"
                name="transferability"
                value="immediate"
                checked={localData.nftTransferability === 'immediate'}
                onChange={(e) => handleTransferabilityChange(e.target.value)}
                className="h-4 w-4 border-brand-primary text-brand-primary focus:ring-brand-primary accent-brand-primary"
              />
              <Label htmlFor="immediate">Immediate</Label>
            </div>
            <div className="flex items-start space-x-2">
              <input
                type="radio"
                id="after-year"
                name="transferability"
                value="after-year"
                checked={localData.nftTransferability === 'after-year'}
                onChange={(e) => handleTransferabilityChange(e.target.value)}
                className="mt-0.5 h-4 w-4 border-brand-primary text-brand-primary focus:ring-brand-primary accent-brand-primary"
              />
              <div>
                <Label htmlFor="after-year">After 1 year</Label>
                <p className="text-xs text-neutral-500 mt-1">
                  12 months after TGE
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="never"
                name="transferability"
                value="never"
                checked={localData.nftTransferability === 'never'}
                onChange={(e) => handleTransferabilityChange(e.target.value)}
                className="h-4 w-4 border-brand-primary text-brand-primary focus:ring-brand-primary accent-brand-primary"
              />
              <Label htmlFor="never">Never (Soul-bound)</Label>
            </div>
            <div className="space-y-6">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="custom"
                  name="transferability"
                  value="custom"
                  checked={localData.nftTransferability === 'custom'}
                  onChange={(e) => handleTransferabilityChange(e.target.value)}
                  className="h-4 w-4 border-brand-primary text-brand-primary focus:ring-brand-primary accent-brand-primary"
                />
                <Label htmlFor="custom">Custom period</Label>
              </div>
              {localData.nftTransferability === 'custom' && (
                <div className="ml-6 space-y-6 p-4 rounded-md py-[0] px-[0]">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label
                        htmlFor="start-datetime"
                        className="text-sm font-medium text-neutral-800"
                      >
                        Start Date & Time (UTC)
                      </Label>
                      <Input
                        id="customStartDateTime"
                        type="datetime-local"
                        value={localData.customStartDateTime}
                        onChange={(e) => handleDateTimeChange(e.target.value)}
                        onFocus={() => {
                          if (!localData.customStartDateTime)
                            handleDateTimeChange(
                              new Date().toISOString().slice(0, 16),
                            )
                        }}
                        placeholder="YYYY-MM-DD HH:MM"
                        required
                        className={`w-full pr-10 ${
                          fieldErrors.customStartDateTime
                            ? 'border border-status-error bg-red-50 focus:border-status-error'
                            : 'border-neutral-200 hover:border-neutral-600 focus:border-neutral-600'
                        } placeholder:text-neutral-400`}
                      />
                      <p className="text-xs text-neutral-400 mt-1">
                        When NFT trading becomes available
                      </p>
                      <div className="flex flex-col gap-1">
                        {fieldErrors.customStartDateTime && isCustom && (
                          <p className="text-xs text-status-error inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {fieldErrors.customStartDateTime}
                          </p>
                        )}
                        {requestChanges?.customStartDateTime && (
                          <p className="text-xs text-status-error inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {requestChanges.customStartDateTime}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {!isSoulBound && (
        <Card id="royaltySplit">
          <CardHeader>
            <CardTitle className="text-lg">Royalty Split</CardTitle>
            <div className="text-sm text-neutral-600">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  Define how royalties from secondary market sales are
                  distributed
                </li>
                <li>
                  A 1% Infra Provider fee will be automatically applied on top
                  of your royalty split
                </li>
              </ul>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
              {[
                { key: 'project', label: 'Project', default: '1%' },
                { key: 'curator', label: 'Curator', default: '1%' },
              ].map(({ key, label, default: defaultValue }) => (
                <div key={key} className="col-span-1">
                  <Label
                    htmlFor={key}
                    className="text-sm font-medium text-neutral-800"
                  >
                    {label}
                  </Label>
                  <div className="relative min-w-0 mt-1">
                    <Input
                      id={key}
                      type="text"
                      inputMode="decimal"
                      step="0.1"
                      value={(localData.royaltySplit as any)[key]}
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      onBlur={(e) => handleBlur(key, e.target.value)}
                      className={`w-full pr-10 ${
                        fieldErrors[key]
                          ? 'border border-status-error bg-red-50 focus:border-status-error'
                          : 'border-neutral-200 hover:border-neutral-600 focus:border-neutral-600'
                      } rounded-md h-10`}
                      aria-describedby={`${key}-helper ${
                        fieldErrors[key] ? `${key}-error` : ''
                      }`}
                    />
                    <span
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500 pointer-events-none"
                      aria-hidden="true"
                    >
                      %
                    </span>
                  </div>
                  <div className="mt-1 space-y-1">
                    <p
                      id={`${key}-helper`}
                      className="text-xs text-neutral-400"
                    >
                      Default: {defaultValue}
                    </p>
                    <div className="flex flex-col gap-1">
                      {fieldErrors[key] && (
                        <p
                          id={`${key}-error`}
                          className="text-xs text-status-error inline-flex items-center gap-1"
                        >
                          <AlertCircle className="w-3 h-3" />
                          {fieldErrors[key]}
                        </p>
                      )}
                      {requestChanges?.[`royaltySplit.${key}`] && (
                        <p className="text-xs text-status-error inline-flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {requestChanges[`royaltySplit.${key}`]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between pt-8">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
